<template>
    <div class="py-4 container-fluid">
        <div class="row">
            <div class="col-md-12">
                <div class="card">
                    <div class="card-header pb-0">
                        <div class="d-flex align-items-center">
                            <p class="mb-0">{{ examId ? 'Editar Parámetros' : 'Crear Nuevo Examen' }}</p>
                        </div>
                    </div>
                    <div class="card-body">

                        <div v-if="loading" class="text-center p-5">
                            <div class="spinner-border text-success" role="status"></div>
                        </div>

                        <form v-else @submit.prevent="saveConfiguration">
                            <h6 class="text-uppercase text-body text-sm font-weight-bolder">Detalles Básicos del Examen</h6>
                            <div class="row">
                                <div class="col-md-3">
                                    <label class="form-control-label">SKU</label>
                                    <argon-input v-model="examData.sku" type="text" placeholder="Ej: LAB-HMG01" :read="!!examId" />
                                    </div>
                                <div class="col-md-5">
                                    <label class="form-control-label">Nombre del Examen</label>
                                    <argon-input v-model="examData.name" type="text" placeholder="Ej: Hemograma Completo" />
                                </div>
                                <div class="col-md-4">
                                    <label class="form-control-label">Precio de Venta</label>
                                    <argon-input v-model="examData.sale_price" type="number" step="0.01" placeholder="0.00" />
                                </div>
                                <div class="col-md-12 mt-3">
                                    <label class="form-control-label">Descripción</label>
                                    <argon-input v-model="examData.description" type="textarea" rows="2" placeholder="Descripción del examen" />
                                </div>
                            </div>

                            <hr class="horizontal dark my-4" />

                            <h6 class="text-uppercase text-body text-sm font-weight-bolder">Asignación de Parámetros y Rangos</h6>

                            <argon-button color="primary" @click="addEmptyParameter" type="button" class="mb-3" :disabled="!examData.id">
                                + Añadir Parámetro
                            </argon-button>

                            <div v-if="!examData.id" class="alert alert-warning text-white" role="alert">
                                Primero **Guarde los Detalles Básicos** del examen arriba para poder asignar parámetros.
                            </div>

                            <div v-else>
                                <table class="table align-items-center mb-0">
                                    <thead>
                                        <tr>
                                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder">Parámetro</th>
                                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder">Sección</th>
                                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder">Orden</th>
                                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder">Rangos (Condiciones)</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(item, index) in examParameters" :key="item.tempId">
                                            <td>
                                                <ArgonLookup
                                                    :options="availableParameters"
                                                    v-model="item.parameter_object"
                                                    display-key="name"
                                                    track-by="id"
                                                    placeholder="Buscar parámetro..."
                                                    @update:modelValue="handleParameterChange(item, $event)"
                                                />
                                            </td>
                                            <td>
                                                <ArgonLookup
                                                    :options="availableSections"
                                                    v-model="item.section_object"
                                                    display-key="name"
                                                    track-by="id"
                                                    placeholder="Asignar sección..."
                                                    @update:modelValue="item.fk_section_id = $event ? $event.id : null"
                                                />
                                            </td>
                                            <td>
                                                <argon-input type="number" v-model="item.display_order" style="width: 80px;" :step="1" />
                                            </td>
                                            <td>
                                                <argon-button color="info" size="sm" type="button" @click="editRanges(item)">
                                                    {{ item.ranges.length || 0 }} Rango(s)
                                                </argon-button>
                                            </td>
                                            <td>
                                                <argon-button color="danger" size="sm" type="button" @click="removeParameter(index)">
                                                    X
                                                </argon-button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <argon-button type="submit" color="success" class="mt-4">
                                {{ examData.id ? 'Actualizar Configuración' : 'Guardar y Continuar' }}
                            </argon-button>
                        </form>
                    </div>
                </div>
                <div v-if="showRangesModal" class="modal-backdrop show">
                <div class="modal-dialog modal-lg modal-dialog-centered" role="document" style="transform: translate(0, 0);">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">
                                Editar Rangos de Referencia
                                <small v-if="currentRangesItem" class="text-muted d-block">
                                    Parámetro: {{ currentRangesItem.parameter_object ? currentRangesItem.parameter_object.name : 'N/A' }}
                                </small>
                            </h5>
                            <button type="button" class="btn-close" @click="showRangesModal = false"></button>
                        </div>

                        <div class="modal-body">
                            <argon-button color="primary" size="sm" @click="addEmptyRange" type="button" class="mb-3">
                                + Añadir Condición
                            </argon-button>

                            <div class="alert alert-info text-white text-sm" v-if="isNumericType">
                                Asegúrese de definir los rangos de edad. El valor será guardado en `min_value` / `max_value`.
                            </div>
                            <div class="alert alert-warning text-dark text-sm" v-else>
                                El parámetro es de tipo **CUALITATIVO**. El valor será guardado en `range_text`.
                            </div>

                            <div v-for="(range, index) in currentRanges" :key="index" class="p-3 mb-3 border rounded">
                                <div class="row align-items-center">
                                    <div class="col-md-2">
                                        <label class="form-control-label">Género</label>
                                        <argon-select :choices="genderOptions" v-model="range.gender" />
                                    </div>

                                    <div class="col-md-3">
                                        <label class="form-control-label">Edad Mín. (Días)</label>
                                        <argon-input v-model="range.age_min_days" type="number" placeholder="0" :step="1" />
                                    </div>
                                    <div class="col-md-3">
                                        <label class="form-control-label">Edad Máx. (Días)</label>
                                        <argon-input v-model="range.age_max_days" type="number" placeholder="99999" :step="1" />
                                    </div>

                                    <div v-if="isNumericType" class="col-md-4 row m-0 p-0">
                                        <div class="col-6">
                                            <label class="form-control-label">Mínimo</label>
                                            <argon-input v-model="range.min_value" type="number" placeholder="0.0" :step="0.01" />
                                        </div>
                                        <div class="col-6">
                                            <label class="form-control-label">Máximo</label>
                                            <argon-input v-model="range.max_value" type="number" placeholder="0.0" :step="0.01" />
                                        </div>
                                    </div>
                                    <div v-else class="col-md-4">
                                        <label class="form-control-label">Valor Esperado/Texto</label>
                                        <argon-input v-model="range.range_text" type="text" placeholder="Ej: NEGATIVO o AUSENTE" />
                                    </div>

                                    <div class="col-md-1 mt-3 text-end">
                                        <argon-button color="danger" size="sm" @click="removeRange(index)" type="button">
                                            Eliminar
                                        </argon-button>
                                    </div>
                                </div>
                            </div>

                            <div v-if="currentRanges.length === 0" class="text-center text-muted py-3">
                                No hay rangos definidos para este parámetro.
                            </div>
                        </div>

                        <div class="modal-footer">
                            <argon-button color="light" @click="showRangesModal = false">Cancelar</argon-button>
                            <argon-button color="success" @click="saveRanges()">Guardar Rangos</argon-button>
                        </div>
                    </div>
                </div>
            </div>
            </div>

        </div>

    </div>
</template>
<script>
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import ArgonLookup from "@/components/ArgonLookup.vue";
import axios from 'axios';
import Swal from 'sweetalert2';
import { useStatics } from '@/composables/useStatics';

const statics = useStatics();
let uniqueIdCounter = 0;

export default {
    name: "CreateExamParameterization", // 🛑 Renombrado para Claridad 🛑
    components: { ArgonInput, ArgonButton, ArgonLookup },
    data() {
        // Obtenemos el company ID una sola vez
        const companyId = parseInt(localStorage.getItem('companyId'));

        return {
            loading: true,
            examId:parseInt(this.$route.params.id),
            // Datos del examen/producto (FASE 1)
            examData: {
                id: this.examId,
                name: '',
                sku: '',
                sale_price: 0.00,
                description: '',
                is_lab_test: true, // Siempre TRUE en esta vista
                taxable: true,
                state: 1,
                unit_id: null, // Asumimos una unidad por defecto o se añade input si es necesario
                category: null, // Asumimos una categoría por defecto (ej: EXÁMENES BIOQUÍMICOS)
                company: companyId,
            },

            // Datos de la parametrización (FASE 2)
            examParameters: [],
            showRangesModal: false,
            currentRangesItem: null, // Objeto completo (parámetro) que estamos editando
            currentRanges: [], // Array de rangos anidados (ex_reference_rangos)
            genderOptions: [
                { value: 'A', text: 'Ambos' },
                { value: 'M', text: 'Masculino' },
                { value: 'F', text: 'Femenino' },
            ],
            // Datos estáticos
            availableParameters: statics.ex_parameters,
            availableSections: statics.ex_sections,
            examDetail: { name: '', sku: '' }, // Usado para display
        };
    },
    computed:{
        isNumericType() {
            if (!this.currentRangesItem || !this.currentRangesItem.parameter_object) return false;
            const dataType = this.currentRangesItem.parameter_object.data_type;
            return dataType === 'NUMERIC';
        }
    },
    async mounted() {
        if (this.examId) {
            await this.fetchExamConfiguration();
        } else {
            // Si es creación, solo inicializamos los datos de prueba
            this.loading = false;
        }
    },

    methods: {
        async fetchAvailableParameters(config) {
            const apiURL = this.$store.state.apiURL;
            try {
                const result = await axios.get(`${apiURL}ex_parameters`, config);
                // Mapeo simple: id y name son necesarios para ArgonLookup
                this.availableParameters = result.data.map(p => ({ id: p.id, name: p.name, value: p.id }));
            } catch (error) {
                console.error("Fallo al cargar Parámetros:", error);
            }
        },

        async fetchAvailableSections(config) {
            const apiURL = this.$store.state.apiURL;
            try {
                const result = await axios.get(`${apiURL}ex_sections`, config);
                console.log(result);
                this.availableSections = result.data.map(s => ({ id: s.id, name: s.name, value: s.id }));
            } catch (error) {
                console.error("Fallo al cargar Secciones:", error);
            }
        },
        async fetchExamConfiguration() {
            this.loading = true;
            const token = localStorage.getItem("accessToken");
            const apiURL = this.$store.state.apiURL;
            const config = { headers: { Authorization: `Bearer ${token}` } };
            await Promise.all([
                this.fetchAvailableParameters(config),
                this.fetchAvailableSections(config)
            ]);
            try {
                // 1. Obtener detalles del examen (products)
                const examResult = await axios.get(`${apiURL}products/${this.examId}`, { headers: { Authorization: `Bearer ${token}` } });
                this.examData = examResult.data;
                this.examDetail = examResult.data;

                const configResult = await axios.get(`${apiURL}lab/exam_config/${this.examId}`, { headers: { Authorization: `Bearer ${token}` } });
                console.log(configResult);
                const parameterData = configResult.data;
                if (     parameterData.length > 0) {
                    // A. Si hay datos, mapearlos (flujo normal de edición)
                    this.examParameters = parameterData.map(p => ({
                        ...p,
                        tempId: uniqueIdCounter++,
                        parameter_object: this.availableParameters.find(pm => pm.id === p.fk_parameter_id),
                        section_object: this.availableSections.find(s => s.id === p.fk_section_id),
                        ranges: p.ranges || [],
                    }));
                } else {
                    this.examParameters = [];
                    this.addEmptyParameter();
                }


            } catch (error) {
                console.error("Error al cargar la configuración:", error);
                Swal.fire('Error', 'No se pudo cargar la configuración del examen. Verifique el endpoint /lab/exam_config.', 'error');
            } finally {
                this.loading = false;
            }
        },

        addEmptyParameter() {
            if (!this.examData.id) {
                 Swal.fire('Advertencia', 'Guarde el examen primero.', 'warning');
                 return;
            }
            this.examParameters.push({
                fk_parameter_id: null,
                fk_section_id: null,
                display_order: this.examParameters.length + 1,
                parameter_object: null,
                section_object: null,
                ranges: [],
                tempId: uniqueIdCounter++,
            });
        },

        removeParameter(index) {
            this.examParameters.splice(index, 1);
        },

        handleParameterChange(item, selectedObject) {
            item.parameter_object = selectedObject;
            item.fk_parameter_id = selectedObject ? selectedObject.id : null;
        },
        editRanges(parameterItem) {
            // Asigna el ítem completo y copia el array de rangos para la edición temporal
            this.currentRangesItem = parameterItem;
            // Creamos una copia profunda para que los cambios no afecten el modelo principal hasta guardar
            this.currentRanges = JSON.parse(JSON.stringify(parameterItem.ranges || []));
            this.showRangesModal = true;
        },
        addEmptyRange() {
            this.currentRanges.push({
                id: null, // Será null para nuevos registros
                gender: 'A', // Valor por defecto
                age_min_days: 0,
                age_max_days: 99999,
                min_value: null,
                max_value: null,
                range_text: null,
            });
        },
        removeRange(index) {
            this.currentRanges.splice(index, 1);
        },
        saveRanges() {
            // 1. Clonar el array de rangos editados de vuelta al modelo principal
            this.currentRangesItem.ranges = this.currentRanges;
            // 2. Cerrar el modal
            this.showRangesModal = false;

            // NOTA: El guardado final a la base de datos se realiza en saveConfiguration().
            // Este método solo actualiza el modelo del componente.
        },
        // 🛑 LÓGICA DE SUBMIT (POST/PUT) 🛑
        async saveConfiguration() {
            const token = localStorage.getItem("accessToken");
            const apiURL = this.$store.state.apiURL;

            // 🛑 VALIDACIÓN NECESARIA 🛑
            // Asumiendo que has añadido this.submitted = true; and this.v$.$touch();
            // y que la validación Vuelidate se ejecuta aquí.

            /*
            if (this.v$.$invalid) {
                Swal.fire('Advertencia', 'Revise los detalles del examen.', 'warning');
                return;
            }
            */

            // 1. Fase: Validar y Crear/Actualizar Producto
            let examId = this.examData.id;
            const productUrl = `${apiURL}products${examId ? '/' + examId : ''}`;
            const method = examId ? 'PUT' : 'POST';

            try {
                // Crear o Actualizar el Producto (Tabla 'products')
                const productResponse = await axios({
                    method: method,
                    url: productUrl,
                    data: this.examData, // Payload del producto
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
                });

                // Si fue creación, obtenemos el nuevo ID para la Fase 2
                if (method === 'POST') {
                    examId = productResponse.data.id;
                    this.examData.id = examId; // Actualiza el modelo para habilitar la tabla
                    this.examDetail = productResponse.data;
                }

                // 2. Fase: Parametrización (Solo si hay parámetros)
                // La lógica de mapeo debe ser fuera del if(examParameters.length > 0) para que payload
                // esté disponible para el siguiente if, o manejado dentro. Lo manejaremos dentro.

                if (this.examParameters.length > 0) {

                    const payload = this.examParameters.map(p => {
                        // 🛑 CORRECCIÓN: Desestructuración y Limpieza. Elimina el console.log que causaba el error.
                        const { parameter_object, section_object, tempId, ...rest } = p;
                        console.log(parameter_object, section_object, tempId)
                        // Validación de FKs (para evitar envíos incompletos)
                        if (!rest.fk_parameter_id || !rest.fk_section_id) {
                            Swal.fire('Advertencia', 'Faltan parámetros o secciones en algunas filas.', 'warning');
                            return null; // Lo filtraremos después
                        }
                        return rest;
                    }).filter(p => p !== null); // Filtra los elementos nulos (con errores de validación)


                    if (payload.length > 0) {
                        // Endpoint PUT CUSTOM para guardar el array de relaciones
                        const config = { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } };
                        // 🛑 ACCIÓN PUT: Guardado de la configuración paramétrica 🛑
                        await axios.put(`${apiURL}lab/exam_config/${examId}`, payload, config);
                    }
                }

                // 3. Finalización y Notificación
                Swal.fire('Éxito', `Examen ${method === 'POST' ? 'creado y' : 'actualizado'} con éxito.`, 'success');

                // Recargar la configuración para obtener IDs persistentes si fue un POST
                if (method === 'POST') {
                    await this.fetchExamConfiguration();
                }

            } catch (error) {
                console.error("Error en la transacción:", error);
                Swal.fire('Error', 'Fallo en la creación o actualización del examen. Revise el SKU o la conexión del API.', 'error');
            }
        },


    }
};
</script>
<style>
/* Sobrescribe para asegurar que el contenido principal sea opaco y blanco */
.modal-content {
    background-color: #FFFFFF !important;
}

/* Opcional: Si quieres mover el z-index a CSS */
.modal-backdrop.show {
    z-index: 1050;
    background-color: rgba(0, 0, 0, 0.8); /* Usar 0.8 para un fondo más oscuro */
}</style>